import React, { useEffect, useRef } from 'react';
import { Play } from 'lucide-react';
import VocabularyWordCard from './VocabularyWordCard';
import useVocabularyPreviewMotion from '../hooks/useVocabularyPreviewMotion';
import { t } from '../i18n';
import './VocabularyPreview.css';

const v = (key, values) => t(`vocabulary.${key}`, undefined, values);

export default function VocabularyPreview({ cards = [], targetCount = 10, direction, disabled, onExclude, onStart, onEmptySettled }) {
  const { displayed, leaving, transitioning, listRef } = useVocabularyPreviewMotion(cards);
  const locked = disabled || transitioning;
  const startRef = useRef(null);
  const restoreIndexRef = useRef(null);
  useEffect(() => {
    if (locked || displayed !== cards || restoreIndexRef.current === null) return;
    const items = listRef.current?.children;
    const item = items?.[Math.min(restoreIndexRef.current, items.length - 1)];
    const next = item?.querySelector('.vocabulary-preview-known') || item?.querySelector('.vocabulary-preview-card');
    restoreIndexRef.current = null;
    (next || startRef.current)?.focus();
  }, [cards, displayed, locked, listRef]);
  useEffect(() => {
    if (!locked && !cards.length && !displayed.length) onEmptySettled?.();
  }, [cards.length, displayed.length, locked, onEmptySettled]);

  return <section className="vocabulary-preview" aria-label={v('previewTitle')} aria-busy={locked}>
    <div className="vocabulary-preview-heading">
      <h2>{v('previewHeading')}</h2>
      <span>{v('previewCount', { count: cards.length, total: targetCount })}</span>
    </div>
    <p className="vocabulary-preview-description">{v('previewDescription')}</p>
    <ol className="vocabulary-preview-spread" ref={listRef} aria-label={v('previewCards')}>
      {displayed.map((card, index) => {
        return <li key={card.cardId} data-card-id={card.cardId} className={`vocabulary-preview-item${leaving.includes(card.cardId) ? ' is-leaving' : ''}`}>
          <VocabularyWordCard card={card} direction={card.direction || direction} disabled={locked}
            onExclude={() => {
              restoreIndexRef.current = index;
              onExclude(card.cardId);
            }} />
        </li>;
      })}
    </ol>
    <div className="vocabulary-preview-start">
      {cards.length < targetCount && <p role="status">{v('previewShort', { count: cards.length })}</p>}
      <button type="button" ref={startRef} className="vocabulary-primary" disabled={locked || !cards.length} onClick={onStart}>
        <Play size={18} aria-hidden="true" />{v('previewStart', { count: cards.length })}
      </button>
      <small>{v('previewUnscored')}</small>
    </div>
  </section>;
}
